import React from 'react';
import { Link } from 'react-router-dom';

export interface PageTabItem {
  key: string;
  label: string;
  href?: string;
  count?: number;
  disabled?: boolean;
}

export interface PageTabsProps {
  items: PageTabItem[];
  activeKey: string;
  variant: 'primary' | 'secondary';
  onSelect?: (key: string) => void;
  ariaLabel?: string;
}

/** Shared by page tabs and subtabs so the navy underline sits the same distance under the label. */
const TAB_LIST = 'flex items-end gap-5 border-b border-neutral-200 w-fit max-w-full';
const TAB_ITEM =
  'inline-flex items-center gap-2 whitespace-nowrap pb-2 text-sm font-medium border-b-2 -mb-px transition-colors';

function tabStateClass(isActive: boolean): string {
  if (isActive) {
    return 'border-primary-500 text-primary-500';
  }
  return 'border-transparent text-neutral-500 hover:text-primary-500/80';
}

function CountPill({ count, isActive }: { count: number; isActive: boolean }) {
  return (
    <span
      className={[
        'py-0.5 px-2.5 rounded-full text-xs font-medium tabular-nums',
        isActive ? 'bg-primary-100 text-primary-500' : 'bg-neutral-100 text-neutral-800',
      ].join(' ')}
    >
      {count}
    </span>
  );
}

const PageTabs: React.FC<PageTabsProps> = ({
  items,
  activeKey,
  onSelect,
  ariaLabel,
}) => {
  return (
    <nav className={TAB_LIST} role="tablist" aria-label={ariaLabel}>
      {items.map((item) => {
        const isActive = item.key === activeKey;
        const className = [
          TAB_ITEM,
          tabStateClass(isActive),
          item.disabled ? 'cursor-not-allowed opacity-60' : '',
        ]
          .filter(Boolean)
          .join(' ');

        const inner = (
          <>
            {item.label}
            {typeof item.count === 'number' ? <CountPill count={item.count} isActive={isActive} /> : null}
          </>
        );

        if (item.disabled) {
          return (
            <span
              key={item.key}
              role="tab"
              aria-selected={false}
              aria-disabled="true"
              className={className}
            >
              {inner}
            </span>
          );
        }

        if (onSelect) {
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={className}
              onClick={() => onSelect(item.key)}
            >
              {inner}
            </button>
          );
        }

        return (
          <Link
            key={item.key}
            to={item.href || '#'}
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            className={className}
          >
            {inner}
          </Link>
        );
      })}
    </nav>
  );
};

export default PageTabs;
